<script lang="ts">
/** Open dialogs, the newest (on top) last: only it answers Escape. */
const open: symbol[] = []
</script>

<script setup lang="ts">
// A dialog. `page`: a whole screen on phones (CSS only; the same dialog elsewhere), for
// settings and other screens with several sections. `back`: a Back button in the header,
// labelled with where it goes. Escape closes the one on top; Back (and the phone's back
// gesture, where the screen is in the URL) steps out one level.
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import Icon from './Icon.vue'

const props = defineProps<{ title: string; page?: boolean; back?: string }>()
const emit = defineEmits<{ close: []; back: [] }>()
const heading = ref<HTMLElement | null>(null)

const me = Symbol()
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape' && open[open.length - 1] === me) emit('close')
}
onMounted(() => {
  open.push(me)
  window.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  open.splice(open.indexOf(me), 1)
  window.removeEventListener('keydown', onKey)
})
// A new screen in the same dialog: screen readers and keyboards start at its heading.
watch(
  () => props.title,
  () => void nextTick(() => heading.value?.focus()),
)
</script>

<template>
  <div
    class="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4 backdrop-blur-[2px]"
    :class="page ? 'max-md:p-0' : ''"
    @click.self="emit('close')"
  >
    <div
      class="panel scroll-thin max-h-[90vh] w-full overflow-y-auto border-border-strong shadow-[0_24px_64px_rgba(0,0,0,0.6)]"
      :class="page ? 'max-w-lg max-md:h-full max-md:max-h-none max-md:max-w-none max-md:border-0 max-md:shadow-none' : 'max-w-md'"
      role="dialog"
      :aria-label="title"
    >
      <div class="sticky top-0 z-10 flex h-11 items-center gap-2 border-b border-border bg-bg-elevated px-5" :class="page ? 'max-md:h-14' : ''">
        <button
          v-if="back"
          class="mono -ml-2 flex items-center gap-1 px-1 py-1 text-text-muted uppercase hover:text-text-primary"
          :aria-label="`Back to ${back}`"
          data-testid="modal-back"
          @click="emit('back')"
        >
          <Icon name="left" />
        </button>
        <h2 ref="heading" tabindex="-1" class="mono min-w-0 flex-1 truncate tracking-[0.14em] text-text-primary uppercase outline-none">{{ title }}</h2>
        <button class="mono -mr-2 px-2 py-1 text-text-muted hover:text-text-primary" aria-label="Close" @click="emit('close')">ESC ✕</button>
      </div>
      <div class="p-5">
        <slot />
      </div>
    </div>
  </div>
</template>
